import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import Modal from '../common/Modal';

/**
 * Modal pour téléverser le scan de la décharge signée (étape 2)
 */
const DischargeUploadModal = ({ requestId, documentUrl, onClose, onUploaded, documentId }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState(null);
  const [docId, setDocId] = useState(null);

  useEffect(() => {
    // Extraire l'ID du document à partir de l'URL si disponible
    if (documentUrl && !documentId) {
      const parts = documentUrl.split('/');
      const filename = parts[parts.length - 1];
      if (filename.startsWith('decharge_') && filename.endsWith('.pdf')) {
        const requestIdFromFilename = filename.replace('decharge_', '').replace('.pdf', '');
        // Rechercher le document par requestId
        const fetchDocumentId = async () => {
          try {
            const response = await api.documents.getByRequest(requestId);
            const documents = response.data.data;
            const dechargeDoc = documents.find(doc => doc.type === 'decharge');
            if (dechargeDoc) {
              setDocId(dechargeDoc._id);
            }
          } catch (error) {
            console.error('Error fetching document ID:', error);
          }
        };
        fetchDocumentId();
      }
    } else if (documentId) {
      setDocId(documentId);
    }
  }, [documentUrl, documentId, requestId]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) {
      setFile(null);
      setPreview(null);
      return;
    }
    
    // Vérifier le type de fichier
    const validTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!validTypes.includes(selectedFile.type)) {
      setError('Type de fichier non valide. Veuillez sélectionner une image (JPG, PNG) ou un PDF.');
      setFile(null);
      setPreview(null);
      return;
    }
    
    // Vérifier la taille du fichier (max 10 Mo)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('Le fichier est trop volumineux. La taille maximale est de 10 Mo.');
      setFile(null);
      setPreview(null);
      return;
    }
    
    setFile(selectedFile);
    setError('');
    
    // Générer un aperçu pour les images
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreview(e.target.result);
      };
      reader.readAsDataURL(selectedFile);
    } else {
      // Pour les PDF, afficher une icône
      setPreview('pdf');
    }
  };

  const handleDownload = async () => {
    if (!docId) {
      toast.error('ID du document non disponible');
      return;
    }
    
    setDownloadLoading(true);
    
    try {
      const response = await api.documents.download(docId);
      
      // Créer un blob à partir de la réponse
      const blob = new Blob([response.data], { type: 'application/pdf' });
      
      // Créer une URL pour le blob
      const url = window.URL.createObjectURL(blob);
      
      // Créer un lien et cliquer dessus pour télécharger
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `decharge_${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Téléchargement réussi');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erreur lors du téléchargement du document');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Veuillez sélectionner un fichier');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Créer un objet FormData
      const formData = new FormData();
      formData.append('dischargeScan', file);
      
      // Appel API pour téléverser le scan
      const response = await api.forceDeVente.uploadScan(requestId, formData);
      
      toast.success('Scan de décharge téléversé avec succès');
      
      // Notifier le composant parent
      if (onUploaded) {
        onUploaded(response.data.data);
      }
      
      // Fermer la modal
      onClose();
    } catch (error) {
      console.error('Error uploading discharge scan:', error);
      setError(error.response?.data?.error || 'Erreur lors du téléversement');
      toast.error(error.response?.data?.error || 'Erreur lors du téléversement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Téléverser le scan de la décharge signée"
      size="md"
      footer={
        <>
          <button
            type="button"
            className="modal-btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            form="discharge-upload-form"
            className="modal-btn-success"
            disabled={loading || !file}
          >
            {loading ? 'Téléversement...' : 'Téléverser et valider'}
          </button>
        </>
      }
    >
      <form id="discharge-upload-form" onSubmit={handleSubmit}>
        <p className="text-sm text-gray-500 mb-4">
                      Veuillez télécharger la décharge, l'imprimer, la faire signer par l'animateur et le distributeur, puis téléverser le scan de la décharge signée.
                    </p>
                    
                    {error && (
                      <p className="mt-2 text-sm text-red-600">{error}</p>
                    )}
                    
                    <div className="mb-6 mt-4">
                      <button
                        type="button"
                        onClick={handleDownload}
                        disabled={downloadLoading}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        {downloadLoading ? (
                          <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        )}
                        Télécharger la décharge
                      </button>
                    </div>
                    
                    <div className="mb-4">
                      <label htmlFor="dischargeScan" className="block text-sm font-medium text-gray-700">
                        Scan de la décharge signée *
                      </label>
                      <input
                        type="file"
                        id="dischargeScan"
                        name="dischargeScan"
                        onChange={handleFileChange}
                        accept="image/jpeg,image/png,application/pdf"
                        className="mt-1 focus:ring-green-500 focus:border-green-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Formats acceptés : JPG, PNG, PDF. Taille maximale : 10 Mo.
                      </p>
                    </div>
                    
                    {preview && (
                      <div className="mt-4 border border-gray-200 rounded-md p-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Aperçu :</p>
                        {preview === 'pdf' ? (
                          <div className="flex items-center justify-center bg-gray-50 h-32 rounded">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        ) : (
                          <img src={preview} alt="Aperçu" className="max-h-32 mx-auto" />
                        )}
          </div>
        )}
      </form>
    </Modal>
  );
};

export default DischargeUploadModal;
